import React, { useState } from 'react';
import { Button } from '@components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@components/ui/dialog';
import { toast } from '@core/toast';
import { Checkbox } from '../../../components/checkbox';
import { DateTimePicker } from '../../../components/date-picker';
import { Select } from '../../../components/select';
import {
    FitnessAppBackfillRequestDto,
    UserFitnessAppSupportDto
} from '../../../core/api/MobileClient';

interface Props {
    open: boolean;
    onClose: () => void;
    userId: number;
    app: BackfillableApp | null;
    onBackfill: (request: FitnessAppBackfillRequestDto) => Promise<void>;
}

export interface BackfillableApp {
    fitnessApp: UserFitnessAppSupportDto['fitnessApp'];
    name: string;
}

const startOfDay = (date: Date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate());

const endOfDay = (date: Date) =>
    new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        23,
        59,
        59,
        999
    );

const HelperText = ({ children }: { children: React.ReactNode }) => (
    <p className="text-xs text-muted-foreground">{children}</p>
);

export const BackfillModal: React.FC<Props> = ({
    open,
    onClose,
    userId,
    app,
    onBackfill
}) => {
    const [fromDate, setFromDate] = useState<Date | null>(null);
    const [toDate, setToDate] = useState<Date | null>(null);
    const [steps, setSteps] = useState(true);
    const [mode, setMode] = useState<'PUSH' | 'PULL'>('PUSH');
    const [stepsMode, setStepsMode] = useState<'DAILY' | 'CHUNKED'>('CHUNKED');
    const [workoutsMode, setWorkoutsMode] = useState<'SUMMARY' | 'DETAILS'>(
        'SUMMARY'
    );
    const [workouts, setWorkouts] = useState(true);
    const [dryRun, setDryRun] = useState(false);
    const [loading, setLoading] = useState(false);

    const supportsSteps = app?.fitnessApp !== 'STRAVA';

    const canSubmit =
        app !== null &&
        fromDate !== null &&
        toDate !== null &&
        ((supportsSteps && steps) || workouts) &&
        !loading;

    const handleClose = () => {
        if (loading) {
            return;
        }

        onClose();
    };

    const handleSubmit = async () => {
        if (!fromDate || !toDate || !app) {
            return;
        }

        setLoading(true);
        try {
            await onBackfill({
                userIds: [userId],
                fitnessApps: [app.fitnessApp],
                dateTimeRange: {
                    from: fromDate.toISOString(),
                    to: toDate.toISOString()
                },
                steps: supportsSteps && steps,
                workouts,
                dryRun,
                mode,
                stepsMode,
                workoutsMode
            });

            toast.success(`${app.name} backfill started`);
            onClose();
        } catch (error: any) {
            toast.error(
                error?.error?.message ||
                    error?.response?.data?.message ||
                    'Failed to start backfill'
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>Backfill {app?.name} data</DialogTitle>
                </DialogHeader>
                <div className="flex flex-col gap-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                        <DateTimePicker
                            label="From"
                            value={fromDate}
                            onChange={(value) =>
                                setFromDate(
                                    value && !fromDate
                                        ? startOfDay(value)
                                        : value
                                )
                            }
                            maxDate={toDate ?? undefined}
                            disabled={loading}
                        />
                        <DateTimePicker
                            label="To"
                            value={toDate}
                            onChange={(value) =>
                                setToDate(
                                    value && !toDate ? endOfDay(value) : value
                                )
                            }
                            minDate={fromDate ?? undefined}
                            disabled={loading}
                        />
                    </div>
                    <div className="flex flex-col gap-2">
                        <Checkbox
                            center
                            checked={supportsSteps && steps}
                            onChange={(e) => setSteps(e.target.checked)}
                            disabled={loading || !supportsSteps}
                        >
                            Daily steps
                        </Checkbox>
                        <Checkbox
                            center
                            checked={workouts}
                            onChange={(e) => setWorkouts(e.target.checked)}
                            disabled={loading}
                        >
                            Workouts
                        </Checkbox>
                        <Checkbox
                            center
                            checked={dryRun}
                            onChange={(e) => setDryRun(e.target.checked)}
                            disabled={loading}
                        >
                            Dry run - log only, call nothing
                        </Checkbox>
                    </div>
                    {app?.fitnessApp === 'GARMIN' && (
                        <div className="flex flex-col gap-4">
                            <div className="flex flex-col gap-1">
                                <Select
                                    label="Mode"
                                    value={mode}
                                    options={[
                                        {
                                            label: 'Push - request from Garmin',
                                            value: 'PUSH'
                                        },
                                        {
                                            label: 'Pull - fetch directly',
                                            value: 'PULL'
                                        }
                                    ]}
                                    onChange={setMode}
                                    disabled={loading}
                                />
                                <HelperText>
                                    Push asks Garmin to re-send via webhooks.
                                    Pull fetches directly and ingests
                                    immediately.
                                </HelperText>
                            </div>
                            {steps && (
                                <div className="flex flex-col gap-1">
                                    <Select
                                        label="Steps source"
                                        value={stepsMode}
                                        options={[
                                            {
                                                label: 'Epochs - 15 minute slices',
                                                value: 'CHUNKED'
                                            },
                                            {
                                                label: 'Dailies - daily totals',
                                                value: 'DAILY'
                                            }
                                        ]}
                                        onChange={setStepsMode}
                                        disabled={loading}
                                    />
                                    <HelperText>
                                        Epochs are 15-minute slices. Dailies are
                                        whole-day totals.
                                    </HelperText>
                                </div>
                            )}
                            {workouts && (
                                <div className="flex flex-col gap-1">
                                    <Select
                                        label="Workouts source"
                                        value={workoutsMode}
                                        options={[
                                            {
                                                label: 'Activities - summaries',
                                                value: 'SUMMARY'
                                            },
                                            {
                                                label: 'Activity details - with GPS',
                                                value: 'DETAILS'
                                            }
                                        ]}
                                        onChange={setWorkoutsMode}
                                        disabled={loading}
                                    />
                                    <HelperText>
                                        Activities are summaries only. Activity
                                        details also carry GPS samples.
                                    </HelperText>
                                </div>
                            )}
                        </div>
                    )}
                    {!supportsSteps && (
                        <HelperText>
                            Strava has no daily steps - it only stores workouts,
                            so the steps option does not apply.
                        </HelperText>
                    )}
                    {app?.fitnessApp === 'ZEPP' && steps && (
                        <HelperText>
                            Zepp ignores the date range for daily steps and
                            returns only the days it has already synced. The
                            range still applies to workouts.
                        </HelperText>
                    )}
                </div>
                <DialogFooter>
                    <Button
                        variant="ghost"
                        onClick={handleClose}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        loading={loading}
                        disabled={!canSubmit}
                    >
                        Run backfill
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
